import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate } from '../lib/supabase'

const fmt = (n) => Math.round(n || 0).toLocaleString()

const QuickAction = ({ icon, label, to, primary }) => {
  const navigate = useNavigate()
  return (
    <button onClick={() => navigate(to)}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 10px', background: primary ? 'var(--green-deep)' : 'var(--cream)', border: `1px solid ${primary ? 'var(--green-deep)' : 'var(--border)'}`, borderRadius: 10, cursor: 'pointer', flex: 1, minWidth: 80, transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.opacity = '0.85' }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
    >
      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      <span style={{ fontSize: '0.68rem', color: primary ? 'white' : 'var(--text-muted)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </button>
  )
}

export default function DashboardOps() {
  const { profile } = useAuth()
  const [data, setData] = useState({})
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const today = ugandaDate()

    const [tasksRes, harvestRes, stockRes, incidentRes, invoicesRes, cashRes] = await Promise.all([
      supabase.from('tasks').select('*').in('status', ['Pending', 'In Progress']).order('due_date'),
      supabase.from('harvest_logs').select('grade_a_kg,grade_b_kg,reject_kg,harvest_date').eq('harvest_date', today),
      supabase.from('stock_balances').select('name,current_balance,reorder_level,unit').filter('needs_reorder', 'eq', true),
      supabase.from('incident_reports').select('id,status').in('status', ['Open', 'Under Review']),
      supabase.from('invoices').select('id,status,balance_due').in('status', ['Unpaid', 'Partial']),
      supabase.from('cash_logs').select('total_income,total_expenses,net,log_date').eq('log_date', today),
    ])

    const todayTasks = (tasksRes.data || []).filter(t => t.due_date === today)
    const overdueTasks = (tasksRes.data || []).filter(t => t.due_date < today)
    const todayHarvest = (harvestRes.data || []).reduce((s, h) => s + (parseFloat(h.grade_a_kg) || 0) + (parseFloat(h.grade_b_kg) || 0), 0)
    const cashToday = (cashRes.data || []).reduce((s, c) => s + (parseFloat(c.total_income) || 0), 0)
    const pendingOrders = (invoicesRes.data || []).filter(i => i.status === 'Unpaid').length

    setData({
      todayTasks: todayTasks.length,
      overdueTasks: overdueTasks.length,
      todayHarvest,
      lowStockCount: (stockRes.data || []).length,
      lowStockItems: stockRes.data || [],
      openIncidents: (incidentRes.data || []).length,
      cashToday,
      pendingOrders,
    })
    setTasks((tasksRes.data || []).slice(0, 6))
    setLoading(false)
  }

  const KPI = ({ label, value, sub, color = 'var(--text)', alert, icon }) => (
    <div className="kpi" style={{ background: alert ? 'var(--red-pale)' : undefined, borderLeft: alert ? '3px solid var(--red)' : undefined, cursor: 'default' }}>
      {icon && <div style={{ fontSize: '1.2rem', marginBottom: 4 }}>{icon}</div>}
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color, fontSize: '1.1rem' }}>{value}</div>
      {sub && <div className="kpi-sub" style={{ color: alert ? 'var(--red)' : undefined }}>{sub}</div>}
    </div>
  )

  return (
    <AppLayout title="Operations Dashboard">
      <div style={{ padding: '24px 24px 40px' }}>
        {/* Welcome */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.4rem', color: 'var(--green-deep)', marginBottom: 2 }}>
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.full_name?.split(' ')[0]} 👋
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Here's what needs your attention today.</p>
        </div>

        {/* KPI Strip */}
        <div className="kpi-grid" style={{ marginBottom: 24 }}>
          <KPI icon="✅" label="Tasks Due Today" value={data.todayTasks || 0} color="var(--green-deep)" />
          <KPI icon="⚠️" label="Overdue Tasks" value={data.overdueTasks || 0} alert={data.overdueTasks > 0} color={data.overdueTasks > 0 ? 'var(--red)' : 'var(--text-muted)'} sub={data.overdueTasks > 0 ? 'need attention' : 'all on track'} />
          <KPI icon="🌾" label="Harvest Today" value={`${fmt(data.todayHarvest)} kg`} color="var(--green-mid)" />
          <KPI icon="📦" label="Low Stock Alerts" value={data.lowStockCount || 0} alert={data.lowStockCount > 0} color={data.lowStockCount > 0 ? 'var(--amber)' : 'var(--text-muted)'} />
          <KPI icon="🚨" label="Open Incidents" value={data.openIncidents || 0} alert={data.openIncidents > 0} color={data.openIncidents > 0 ? 'var(--red)' : 'var(--text-muted)'} />
          <KPI icon="💵" label="Cash In Today" value={`UGX ${fmt(data.cashToday)}`} color="var(--green-mid)" />
        </div>

        {/* Quick Actions */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>⚡ Quick Actions</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { icon: '✅', label: 'Create Task', to: '/tasks', primary: true },
              { icon: '🌱', label: 'Farm Log', to: '/farm-log' },
              { icon: '🌾', label: 'Record Harvest', to: '/harvest' },
              { icon: '🛒', label: 'Sales Order', to: '/sales-orders' },
              { icon: '📄', label: 'Raise Invoice', to: '/invoices' },
              { icon: '💵', label: 'Record Cash', to: '/cash-log' },
              { icon: '📋', label: 'Requisition', to: '/requisitions' },
              { icon: '📥', label: 'Goods Received', to: '/goods-received' },
              { icon: '🚨', label: 'Report Incident', to: '/incidents' },
            ].map(a => <QuickAction key={a.to} {...a} />)}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Today's Tasks */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title">📋 Today's Tasks</div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tasks')}>View All</button>
            </div>
            {tasks.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: '16px 0', textAlign: 'center' }}>No tasks due today</div>
            ) : tasks.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.title}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{t.assigned_to_name || 'Unassigned'}</div>
                </div>
                <span className={`badge ${t.status === 'In Progress' ? 'badge-blue' : t.due_date < ugandaDate() ? 'badge-red' : 'badge-amber'}`} style={{ fontSize: '0.62rem' }}>
                  {t.status}
                </span>
              </div>
            ))}
          </div>

          {/* Low Stock Alerts */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title">📦 Low Stock Alerts</div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/inventory')}>View All</button>
            </div>
            {data.lowStockItems?.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: '16px 0', textAlign: 'center' }}>✅ All stock levels OK</div>
            ) : data.lowStockItems?.slice(0, 5).map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem' }}>
                <div style={{ fontWeight: 600 }}>{item.name}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontFamily: "'DM Mono',monospace", color: 'var(--red)', fontSize: '0.75rem' }}>{parseFloat(item.current_balance).toFixed(1)} {item.unit}</span>
                  <span className="badge badge-red" style={{ fontSize: '0.6rem' }}>Low</span>
                </div>
              </div>
            ))}
            {data.lowStockItems?.length > 0 && (
              <button className="btn btn-primary btn-sm" style={{ marginTop: 10, width: '100%' }} onClick={() => navigate('/requisitions')}>
                + Raise Requisition
              </button>
            )}
          </div>

          {/* Workflow shortcuts */}
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-title" style={{ marginBottom: 12 }}>🔄 Workflow Shortcuts</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { title: 'Harvest → Sale', steps: ['Record Harvest', 'Sales Order', 'Invoice', 'Cash Receipt'], icon: '🌾', to: '/harvest' },
                { title: 'Purchase → Payment', steps: ['Requisition', 'Purchase Order', 'Goods Received', 'Pay Invoice'], icon: '📦', to: '/requisitions' },
                { title: 'Daily Cash', steps: ['Record Income', 'Record Expenses', 'Reconcile'], icon: '💵', to: '/cash-log' },
              ].map(flow => (
                <div key={flow.title} onClick={() => navigate(flow.to)} style={{ padding: '12px 14px', background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--cream)'}
                >
                  <div style={{ fontSize: '1.3rem', marginBottom: 6 }}>{flow.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>{flow.title}</div>
                  {flow.steps.map((step, i) => (
                    <div key={i} style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: 'var(--green-bright)' }}>{i + 1}.</span> {step}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
